import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  BulkAction,
  BulkScope,
  BulkApplyResult,
  ExportLicense,
} from 'api/ExportHubAPI';
import { JURISDICTION_OPTIONS, STATUS_OPTIONS } from './productPresets';
import StyledSingleSelect from './StyledSingleSelect';

interface Props {
  open: boolean;
  action: BulkAction | null;
  databaseId: string;
  groupId: string | undefined;
  scope: BulkScope;
  count: number;
  onClose: () => void;
  onApplied: (_result: BulkApplyResult, _action: BulkAction) => void;
}

const TITLES: Record<string, string> = {
  set_jurisdiction: 'Set Jurisdiction',
  set_country_of_origin: 'Set Country of Origin',
  set_compliance_status: 'Set Compliance Status',
  link_license: 'Link License',
};

const BulkApplyModal: React.FC<Props> = ({
  open,
  action,
  databaseId,
  groupId,
  scope,
  count,
  onClose,
  onApplied,
}) => {
  const [value, setValue] = useState('');
  const [licenses, setLicenses] = useState<ExportLicense[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !action) return;
    setValue(
      action === 'set_jurisdiction'
        ? JURISDICTION_OPTIONS[0]
        : action === 'set_compliance_status'
        ? STATUS_OPTIONS[0]
        : '',
    );
    if (action === 'link_license' && groupId) {
      ExportHubAPI.getLicenses(databaseId, groupId)
        .then((d) => {
          setLicenses(d);
          if (d.length) setValue(d[0].id);
        })
        .catch(() => setLicenses([]));
    }
  }, [open, action, databaseId, groupId]);

  if (!action) return null;

  const paramsFor = (): Record<string, unknown> => {
    switch (action) {
      case 'set_jurisdiction':
        return { jurisdiction: value };
      case 'set_country_of_origin':
        return { country_of_origin: value };
      case 'set_compliance_status':
        return { compliance_status: value };
      case 'link_license':
        return { license_id: value };
      default:
        return {};
    }
  };

  const handleApply = async () => {
    if (!groupId) return;
    setSaving(true);
    try {
      const result = await ExportHubAPI.bulkApply(databaseId, {
        group_id: groupId,
        scope: scope,
        action: action,
        params: paramsFor(),
      });
      onApplied(result, action);
    } catch (err) {
      console.error('Bulk apply failed:', err);
      enqueueSnackbar('Bulk action failed.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label={TITLES[action]}>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>{TITLES[action]}</h2>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          {action === 'set_jurisdiction' && (
            <div className='eh-field'>
              <span className='eh-field-label'>Jurisdiction</span>
              <StyledSingleSelect
                options={JURISDICTION_OPTIONS.map((j) => ({
                  value: j,
                  label: j,
                }))}
                value={value}
                onChange={setValue}
                inline
              />
            </div>
          )}
          {action === 'set_compliance_status' && (
            <div className='eh-field'>
              <span className='eh-field-label'>Compliance Status</span>
              <StyledSingleSelect
                options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                value={value}
                onChange={setValue}
                inline
              />
            </div>
          )}
          {action === 'set_country_of_origin' && (
            <label className='eh-field'>
              <span className='eh-field-label'>Country of Origin</span>
              <input
                className='eh-input'
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder='e.g. United States'
              />
            </label>
          )}
          {action === 'link_license' && (
            <div className='eh-field'>
              <span className='eh-field-label'>License</span>
              <StyledSingleSelect
                options={
                  licenses.length === 0
                    ? [{ value: '', label: 'No licenses' }]
                    : licenses.map((l) => ({
                        value: l.id,
                        label: `${l.license_number || l.license_type} (${
                          l.status
                        })`,
                      }))
                }
                value={value}
                onChange={setValue}
                inline
              />
            </div>
          )}

          <div className='eh-bulk-confirm'>
            This will update <strong>{count.toLocaleString()}</strong> product
            {count === 1 ? '' : 's'}.
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={handleApply}
            disabled={saving || (action === 'link_license' && !value)}
            style={{
              opacity: saving || (action === 'link_license' && !value) ? 0.6 : 1,
            }}
          >
            {saving ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkApplyModal;
